<template>
  <div :class="style.actionContainer">
    <span
      v-if="getActionListHeader() && getActions().length > 0"
      v-html="getActionListHeader()"
    />
    <section :class="style.actionList">
      <div
        :class="style.actionItem"
        v-for="(action, actionIndex) in getActions()"
        :key="getActionKey(action, actionIndex)"
      >
        <video
          v-if="action.img && isVideoPath(action.img)"
          :src="getActionMediaUrl(action.img)"
          autoplay
          muted
          loop
          playsinline
          webkit-playsinline
          preload="auto"
          :style="{
            marginBottom: !getHeaderAction(action) ? 0 : '5px'
          }"
        />

        <div
          v-else-if="action.img"
          :style="{
            backgroundImage: getActionBackgroundImage(action.img),
            marginBottom: !getHeaderAction(action) ? 0 : '5px'
          }"
        />
        <h3
          v-if="String(getHeaderAction(action)).replace(' ', '&nbsp;')"
          v-html="getHeaderAction(action).replaceAll('&nbsp;', ' ')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import style from "./ActionComponent.module.scss";
import {insertNonBreakingSpaces} from "@/scripts/typographer";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {action} from "@/page/quiz/typeQuiz";
import {DOMAIN} from "@/http";
import {defineComponent} from "vue";

type CachedPreview = {
  url: string;
  signature: string;
};

export default defineComponent({
  name: "ActionComponent",
  data() {
    return {
      style,
      actions: [],
      DOMAIN,
      modifyQuiz,
      cachedFilePreviewMap: {} as Record<string, CachedPreview>
    };
  },
  mounted() {
    this.syncCachedFilePreviews();
  },
  beforeUnmount() {
    this.revokeCachedFilePreviews();
  },
  methods: {
    getHeaderAction(action: action) {
      if (!action?.header) return "";
      return insertNonBreakingSpaces(action.header);
    },
    getActionListHeader() {
      if (!this.modifyQuiz("get", "", ["actionHeader"])) return false;
      return insertNonBreakingSpaces(
        this.modifyQuiz("get", "", ["actionHeader"])
      );
    },
    getActions(): action[] {
      const actionList = this.modifyQuiz("get", "", ["actionList"]);

      return Array.isArray(actionList) ? actionList : [];
    },
    getActionKey(action: action, actionIndex: number): string {
      return `${actionIndex}-${action.img || "empty"}`;
    },
    isVideoPath(path = "") {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },
    getActionMediaUrl(path: string) {
      const normalizedPath = String(path || "");
      const previewUrl = this.getCachedPreviewUrl(path);

      if (previewUrl) {
        return previewUrl;
      }

      if (
        normalizedPath.startsWith("blob:") ||
        normalizedPath.startsWith("data:")
      ) {
        return normalizedPath;
      }

      if (
        normalizedPath.startsWith("http://") ||
        normalizedPath.startsWith("https://")
      ) {
        return normalizedPath;
      }

      return this.getServerImageUrl(normalizedPath);
    },
    getActionBackgroundImage(path: string): string | undefined {
      const previewUrl = this.getCachedPreviewUrl(path);

      if (previewUrl) {
        return `url("${previewUrl}")`;
      }

      if (
        path.startsWith("blob:") ||
        path.startsWith("data:") ||
        path.startsWith("http://") ||
        path.startsWith("https://")
      ) {
        return `url("${path}")`;
      }

      if (path) {
        return `url("${this.getServerImageUrl(path)}")`;
      }

      return undefined;
    },
    getCachedPreviewUrl(path: string): string {
      const baseName = this.getFileBaseName(path);

      return this.cachedFilePreviewMap[baseName]?.url || "";
    },
    getFileBaseName(path: string): string {
      return (
        String(path || "")
          .split("/")
          .pop() || ""
      );
    },
    getServerImageUrl(path: string): string {
      const params = new URLSearchParams({name: path});

      return `https://${this.DOMAIN}/api/img?${params.toString()}`;
    },
    getFileSignature(file: File): string {
      return `${file.name}-${file.size}-${file.lastModified}`;
    },
    syncCachedFilePreviews(): void {
      const cachedFileSignatureMap = new Map<string, string>();

      this.cachedFiles.forEach((file) => {
        cachedFileSignatureMap.set(file.name, this.getFileSignature(file));
      });

      Object.keys(this.cachedFilePreviewMap).forEach((fileName) => {
        const currentSignature = cachedFileSignatureMap.get(fileName);
        const cachedPreview = this.cachedFilePreviewMap[fileName];

        if (!currentSignature || currentSignature !== cachedPreview.signature) {
          if (cachedPreview.url?.startsWith("blob:")) {
            URL.revokeObjectURL(cachedPreview.url);
          }

          delete this.cachedFilePreviewMap[fileName];
        }
      });

      this.cachedFiles.forEach((file) => {
        const signature = this.getFileSignature(file);
        const cachedPreview = this.cachedFilePreviewMap[file.name];

        if (cachedPreview?.signature === signature) {
          return;
        }

        if (cachedPreview?.url?.startsWith("blob:")) {
          URL.revokeObjectURL(cachedPreview.url);
        }

        this.cachedFilePreviewMap[file.name] = {
          url: URL.createObjectURL(file),
          signature
        };
      });
    },
    revokeCachedFilePreviews(): void {
      Object.values(this.cachedFilePreviewMap).forEach((preview) => {
        if (preview.url?.startsWith("blob:")) {
          URL.revokeObjectURL(preview.url);
        }
      });

      this.cachedFilePreviewMap = {};
    }
  },
  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },
    mobileVersion() {
      return this.$quizStore.getMobileVersion;
    },
    cachedFiles(): File[] {
      const value = this.$quizStore.getCachedFiles;

      return Array.isArray(value) ? value : [];
    }
  },
  watch: {
    cachedFiles: {
      handler(): void {
        this.syncCachedFilePreviews();
      },
      deep: true
    }
  }
});
</script>
